import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import { X } from 'lucide-react';
import { getCurrentUserRole, getCurrentUserId } from '../../utils/auth';
import { dashboardService, User } from '../../services/dashboardService';
import { paymentLinkService, GenerateLinkRequest, GenerateLinkResponse } from '../../services/paymentLinkService';
import { FIAT_CURRENCIES } from '../../constants/fiatCurrencies';
import { exchangeRateService } from '../../services/exchangeRateService';
import { cn } from '../../utils/cn';
import {
  checkoutPathForSession,
  checkoutUrlForSession,
  extractPaySessionId,
} from '../../utils/paymentCheckoutNavigation';

const BRAND = '#2297D1';

const FIAT_PROVIDER_OPTIONS = [
  { id: 'onramper', label: 'Pay with card / bank' },
  { id: 'moonpay', label: 'MoonPay' },
  { id: 'guardarian', label: 'Guardarian' },
  { id: 'paybis', label: 'Paybis' },
  { id: 'transfi', label: 'TransFi' },
] as const;

function filterLinksByProviders(links: string[], selectedIds: Set<string>): string[] {
  if (selectedIds.size === FIAT_PROVIDER_OPTIONS.length) return links;

  const urls = links.map(extractUrl);
  const allInternalPayLinks = urls.length > 0 && urls.every((url) => /\/pay\/[^/?#]+/i.test(url));

  // Checkout URLs are /pay/:sessionId — provider (MoonPay, Transak, …) is chosen on that page.
  if (allInternalPayLinks) return links;

  return links.filter((link) => {
    const lower = extractUrl(link).toLowerCase();
    return Array.from(selectedIds).some((id) => lower.includes(id));
  });
}

function extractUrl(link: string): string {
  const match = link.match(/https:\/\/[^\s]+/);
  return match ? match[0] : link;
}

function extractApiErrorMessage(err: unknown, fallback: string): string {
  const validationHint =
    'Provider rejected checkout configuration. Verify Transak environment/domain settings (referrerDomain, redirectURL, key approval, and allowed domains).';

  if (err && typeof err === 'object') {
    const maybeAxios = err as { response?: { data?: unknown } };
    const data = maybeAxios.response?.data;
    if (typeof data === 'string' && data.trim()) {
      const message = data.trim();
      if (message.toLowerCase().includes('transak')) return `${message} ${validationHint}`;
      return message;
    }
    if (data && typeof data === 'object') {
      const obj = data as Record<string, unknown>;
      const ex =
        obj.expandedError && typeof obj.expandedError === 'object'
          ? (obj.expandedError as Record<string, unknown>)
          : null;
      const rawCandidate =
        (typeof ex?.technicalMessage === 'string' && ex.technicalMessage.trim())
        || (typeof ex?.message === 'string' && ex.message.trim())
        || (typeof obj.message === 'string' && obj.message.trim())
        || (typeof obj.detail === 'string' && obj.detail.trim())
        || (typeof obj.title === 'string' && obj.title.trim())
        || null;
      if (rawCandidate) {
        const normalized = rawCandidate.toLowerCase();
        if (
          normalized.includes('transak')
          || normalized.includes('referrerdomain')
          || normalized.includes('redirect')
          || normalized.includes('whitelist')
          || normalized.includes('validation')
          || normalized.includes('bad request')
        ) {
          return `${rawCandidate} ${validationHint}`;
        }
        return rawCandidate;
      }
    }
  }
  if (err instanceof Error && err.message.trim()) {
    const message = err.message.trim();
    const normalized = message.toLowerCase();
    if (
      normalized.includes('transak')
      || normalized.includes('referrerdomain')
      || normalized.includes('redirect')
      || normalized.includes('whitelist')
      || normalized.includes('validation')
      || normalized.includes('bad request')
    ) {
      return `${message} ${validationHint}`;
    }
    return message;
  }
  return fallback;
}

const CreatePaymentLinkPage: React.FC = () => {
  const navigate = useNavigate();
  const [fiatCurrency, setFiatCurrency] = useState('USD');
  const [fiatAmount, setFiatAmount] = useState('');
  const [userId, setUserId] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [paymentResult, setPaymentResult] = useState<GenerateLinkResponse | null>(null);
  const [currencySearchTerm, setCurrencySearchTerm] = useState('USD');
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  const [minimumAmount, setMinimumAmount] = useState(0.5);
  const [minimumAmountLoading, setMinimumAmountLoading] = useState(false);
  const currencyDropdownRef = useRef<HTMLDivElement>(null);

  const [selectedProviders, setSelectedProviders] = useState<Set<string>>(
    () => new Set(FIAT_PROVIDER_OPTIONS.map((p) => p.id))
  );

  const userRole = getCurrentUserRole();
  const currentUserId = getCurrentUserId();
  const username = localStorage.getItem('username') || '';
  const avatarLetter = (username.trim().charAt(0) || 'L').toUpperCase();

  const filteredCurrencies = useMemo(
    () =>
      currencySearchTerm.trim() === ''
        ? FIAT_CURRENCIES
        : FIAT_CURRENCIES.filter((c) => c.toLowerCase().includes(currencySearchTerm.toLowerCase())),
    [currencySearchTerm]
  );

  useEffect(() => {
    const loadUsers = async () => {
      if (userRole !== 'Admin' && userRole !== 'SuperAgent') return;
      setUsersLoading(true);
      setUsersError(null);
      try {
        const allUsers = await dashboardService.getUsers();
        if (userRole === 'Admin') {
          setUsers(allUsers.filter((u) => u.role !== 'Admin'));
        } else {
          const currentUser = allUsers.find((u) => u.id.toString() === currentUserId);
          const agentUsers = allUsers.filter((u) => u.role === 'Agent');
          let filtered: User[];
          if (currentUser) {
            filtered = [currentUser, ...agentUsers];
          } else {
            const superAgentUser: User = {
              id: parseInt(currentUserId || '0', 10),
              name: username || 'SuperAgent User',
              email: '',
              phone: '',
              usdtWallet: '',
              role: 'SuperAgent',
            };
            filtered = [superAgentUser, ...agentUsers];
          }
          setUsers(filtered);
          if (currentUserId) setUserId(currentUserId);
          setUsersLoading(false);
          return;
        }
      } catch (e) {
        console.error(e);
        const is429 = axios.isAxiosError(e) && e.response?.status === 429;
        setUsersError(is429 ? 'Too many requests — wait a moment and refresh.' : 'Failed to load users');
      } finally {
        setUsersLoading(false);
      }
    };

    if (userRole === 'Agent' && currentUserId) {
      setUserId(currentUserId);
    }
    loadUsers();
  }, [userRole, currentUserId]);

  useEffect(() => {
    setCurrencySearchTerm(fiatCurrency);
  }, [fiatCurrency]);

  useEffect(() => {
    const run = async () => {
      setMinimumAmountLoading(true);
      try {
        const min = await exchangeRateService.getMinimumAmount(fiatCurrency);
        setMinimumAmount(min);
      } catch {
        setMinimumAmount(0.5);
      } finally {
        setMinimumAmountLoading(false);
      }
    };
    if (fiatCurrency) run();
  }, [fiatCurrency]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (currencyDropdownRef.current && !currencyDropdownRef.current.contains(e.target as Node)) {
        setShowCurrencyDropdown(false);
      }
    };
    if (showCurrencyDropdown) document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showCurrencyDropdown]);

  const handleCurrencySelect = async (currency: string) => {
    setFiatCurrency(currency);
    setCurrencySearchTerm(currency);
    setShowCurrencyDropdown(false);
    try {
      const min = await exchangeRateService.getMinimumAmount(currency);
      setMinimumAmount(min);
    } catch {
      setMinimumAmount(0.5);
    }
  };

  const toggleProvider = (id: string) => {
    setSelectedProviders((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setFormError(null);
  };

  const selectAllProviders = () => {
    setSelectedProviders(new Set(FIAT_PROVIDER_OPTIONS.map((p) => p.id)));
  };

  const clearProviders = () => {
    setSelectedProviders(new Set());
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError(null);

    if (selectedProviders.size === 0) {
      setFormError('Select at least one fiat provider.');
      setSubmitting(false);
      return;
    }

    const amount = parseFloat(fiatAmount);
    if (!fiatAmount || Number.isNaN(amount) || amount < minimumAmount) {
      const minFormatted = exchangeRateService.formatMinimum(fiatCurrency, minimumAmount);
      setFormError(`Please enter a valid amount (minimum ${minFormatted})`);
      setSubmitting(false);
      return;
    }

    if (!userId) {
      setFormError('Please select a user');
      setSubmitting(false);
      return;
    }

    try {
      const requestData: GenerateLinkRequest = {
        userId: parseInt(userId, 10),
        fiatCurrency,
        amount: parseFloat(fiatAmount),
      };
      const result = await paymentLinkService.generatePaymentLinks(requestData);
      const filtered = filterLinksByProviders(result.paymentLinks, selectedProviders);
      if (filtered.length === 0) {
        setFormError(
          'No payment links matched your provider selection. Try selecting more providers or use the full recommended mix.'
        );
        setSubmitting(false);
        return;
      }
      const firstLink = filtered[0];
      setPaymentResult({ ...result, paymentLinks: filtered });
      setShowResult(true);
      if (firstLink) {
        const sessionId = extractPaySessionId(firstLink);
        const checkoutUrl = sessionId ? checkoutUrlForSession(sessionId) : extractUrl(firstLink);
        void copyText(checkoutUrl);
      }
    } catch (err) {
      console.error(err);
      setFormError(extractApiErrorMessage(err, 'Failed to create payment link'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    navigate(-1);
  };

  const inputClass =
    'w-full rounded-lg border border-[#d1d9e0] bg-white px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-[#2297D1] focus:outline-none focus:ring-2 focus:ring-[#2297D1]/25';
  const labelClass = 'mb-1.5 block text-xs font-semibold text-slate-700';

  const sectionShell = 'rounded-xl border border-[#e2e8f0] bg-white p-5 shadow-sm';

  const copyText = async (value: string) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      /* clipboard optional */
    }
  };

  return (
    <div className="homeBg  pb-28 pt-6">
      <div className="mx-auto w-full max-w-7xl bg-white p-5 rounded-2xl blurLayerInteractiveInteractive px-4">
        <nav className="mb-4 text-xs text-slate-500">
          <Link to="/dashboard" className="text-slate-500 hover:text-[#2297D1]">
            Payments
          </Link>
          <span className="mx-2">/</span>
          <span className="font-medium text-slate-700">New Payment</span>
        </nav>

        <div className="mb-6 flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 border-b border-slate-200 pb-4 w-full">
           
            <div>
              <h1 className="mb-0 lg:text-[48px] text-2xl font-bold tracking-tight text-slate-900">Create Payment Link</h1>
            </div>
          </div>
       
        </div>

        <div className="flex flex-col ">
          <div className="flex-1 overflow-y-auto  ">
            {!showResult ? (
              <form id="create-payment-link-form" onSubmit={handleSubmit} className="space-y-5">
                <div className="rounded-xl border border-[#e2e8f0] bg-[#f1f5f9] px-4 py-4">
                  <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                    Payment link composer
                  </p>
                  <p className="mb-2 text-sm font-semibold text-slate-800">Create a payment page without exposing the setup complexity.</p>
                  <p className="mb-0 text-sm leading-relaxed text-slate-600">
                    Choose the currency, amount, recipient, and provider mix here. We will handle the behind-the-scenes link
                    generation and payout routing.
                  </p>
                </div>

                {(usersError || formError) && (
                  <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-800">
                    {usersError || formError}
                  </div>
                )}

                <section className={sectionShell}>
                  <h2 className="mb-1 text-base font-semibold text-slate-900">Basics</h2>
                  <p className="mb-4 text-sm text-slate-600">
                    Start with the currency and amount. Provider availability updates automatically once the amount is valid.
                  </p>
                  <div className="space-y-4">
                    <div>
                      <label className={labelClass}>Fiat Currency</label>
                      <div ref={currencyDropdownRef} className="relative">
                        <input
                          type="text"
                          className={inputClass}
                          value={currencySearchTerm}
                          onChange={(e) => {
                            setCurrencySearchTerm(e.target.value);
                            setShowCurrencyDropdown(true);
                          }}
                          onFocus={() => {
                            setShowCurrencyDropdown(true);
                            setCurrencySearchTerm('');
                          }}
                          placeholder="Search currency..."
                          autoComplete="off"
                        />
                        {showCurrencyDropdown && (
                          <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-48 overflow-y-auto rounded-lg border border-[#e2e8f0] bg-white py-1 shadow-lg">
                            {filteredCurrencies.length > 0 ? (
                              filteredCurrencies.map((c) => (
                                <button
                                  key={c}
                                  type="button"
                                  className="w-full px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50"
                                  onClick={() => handleCurrencySelect(c)}
                                >
                                  {c}
                                </button>
                              ))
                            ) : (
                              <div className="px-3 py-2 text-sm italic text-slate-500">No currencies match</div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className={labelClass}>Fiat Amount</label>
                      <input
                        type="number"
                        step="0.01"
                        min={minimumAmount}
                        className={inputClass}
                        value={fiatAmount}
                        onChange={(e) => {
                          setFiatAmount(e.target.value);
                          setFormError(null);
                        }}
                        placeholder={
                          minimumAmountLoading
                            ? 'Loading...'
                            : `Enter amount (minimum ${exchangeRateService.formatMinimum(fiatCurrency, minimumAmount)})`
                        }
                      />
                      <p className="mt-1.5 text-xs text-slate-500">
                        Minimum for testing is {exchangeRateService.formatMinimum(fiatCurrency, minimumAmount)} (manual USDC deposit path).
                      </p>
                    </div>
                  </div>
                </section>

                <section className={sectionShell}>
                  <h2 className="mb-1 text-base font-semibold text-slate-900">Recipient</h2>
                  <p className="mb-4 text-sm text-slate-600">
                    Choose which account should own this page and appear in the reporting that follows it.
                  </p>
                  {userRole !== 'Agent' ? (
                    <>
                      <label className={labelClass}>Select User</label>
                      {usersLoading ? (
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                          <Spinner animation="border" size="sm" />
                          Loading users...
                        </div>
                      ) : (
                        <select
                          className={inputClass}
                          value={userId}
                          onChange={(e) => {
                            setUserId(e.target.value);
                            setFormError(null);
                          }}
                        >
                          <option value="">Select a User</option>
                          {users.map((u, index) => (
                            <option key={u.id} value={u.id}>
                              {userRole === 'SuperAgent' && index === 0 ? '\u{1F464} ' : ''}
                              {u.name} ({u.role})
                            </option>
                          ))}
                        </select>
                      )}
                    </>
                  ) : (
                    <>
                      <label className={labelClass}>Account</label>
                      <input
                        className={cn(inputClass, 'bg-slate-50')}
                        readOnly
                        value={username ? `${username} (Agent)` : 'Your account'}
                      />
                    </>
                  )}
                </section>

                <section className={sectionShell}>
                  <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h2 className="mb-1 text-base font-semibold text-slate-900">Fiat Providers</h2>
                      <p className="text-sm text-slate-600">
                        Choose which fiat providers should appear on the page. You can keep the recommended mix or narrow it
                        down.
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={selectAllProviders}
                        className="rounded-lg border border-[#d1d9e0] bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        Select All
                      </button>
                      <button
                        type="button"
                        onClick={clearProviders}
                        className="rounded-lg border border-[#d1d9e0] bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {FIAT_PROVIDER_OPTIONS.map((p) => (
                      <label
                        key={p.id}
                        className="flex cursor-pointer items-center gap-3 rounded-lg border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2.5"
                      >
                        <input
                          type="checkbox"
                          checked={selectedProviders.has(p.id)}
                          onChange={() => toggleProvider(p.id)}
                          className="h-4 w-4 rounded border-slate-300 text-[#2297D1] focus:ring-[#2297D1]"
                        />
                        <span className="text-sm font-medium text-slate-800">{p.label}</span>
                      </label>
                    ))}
                  </div>
                  <p className="mt-3 mb-0 text-xs text-slate-500">
                    Provider availability is recalculated automatically as the amount and currency change.
                  </p>
                </section>
              </form>
            ) : (
              paymentResult && (
                <div className="space-y-4">
                  <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
                    Payment links are ready. Share the deposit address for manual funding or open a link below to continue to checkout.
                  </div>
                  <div className="grid gap-3 lg:grid-cols-2">
                    <div className="rounded-xl border border-[#e2e8f0] bg-white p-4 shadow-sm">
                      <p className="mb-1 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Transaction</p>
                      <p className="mb-2 text-lg font-semibold text-slate-900">#{paymentResult.transactionId}</p>
                      <button
                        type="button"
                        onClick={() => navigate('/transactions', { state: { openTransactionId: paymentResult.transactionId } })}
                        className="rounded-lg border border-[#d1d9e0] bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                      >
                        Open in transactions
                      </button>
                    </div>
                    <div className="rounded-xl border border-[#dbeafe] bg-[#eff6ff] p-4 shadow-sm">
                      <p className="mb-1 text-xs font-semibold uppercase tracking-[0.16em] text-[#2297D1]">Deposit wallet address</p>
                      <div className="rounded-lg border border-[#bfdbfe] bg-white p-3 font-mono text-xs break-all text-slate-800">
                        {paymentResult.uniqueWalletAddress || 'No wallet address returned'}
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => void copyText(paymentResult.uniqueWalletAddress)}
                          className="rounded-lg px-3 py-1.5 text-xs font-semibold text-white hover:opacity-95"
                          style={{ backgroundColor: BRAND }}
                        >
                          Copy wallet address
                        </button>
                        <button
                          type="button"
                          onClick={() => navigate('/transactions', { state: { openTransactionId: paymentResult.transactionId } })}
                          className="rounded-lg border border-[#d1d9e0] bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                        >
                          View settlement controls
                        </button>
                      </div>
                    </div>
                  </div>
                  <h2 className="text-lg font-semibold text-slate-900">Your links</h2>
                  <ul className="space-y-3">
                    {paymentResult.paymentLinks.map((link, index) => {
                      const url = extractUrl(link);
                      const sessionId = extractPaySessionId(link);
                      const checkoutUrl = sessionId ? checkoutUrlForSession(sessionId) : url;
                      return (
                        <li
                          key={index}
                          className="rounded-xl border border-[#e2e8f0] bg-[#f8fafc] p-4 text-sm text-slate-800"
                        >
                          <div className="mb-2 font-mono text-xs break-all text-slate-700">{checkoutUrl}</div>
                          <div className="flex flex-wrap gap-2">
                            {sessionId && (
                              <button
                                type="button"
                                onClick={() => navigate(checkoutPathForSession(sessionId))}
                                className="rounded-lg px-3 py-1.5 text-xs font-semibold text-white hover:opacity-95"
                                style={{ backgroundColor: BRAND }}
                              >
                                Open checkout
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => void copyText(checkoutUrl)}
                              className="rounded-lg px-3 py-1.5 text-xs font-semibold text-[#2297D1] hover:bg-white"
                            >
                              Copy
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                  <div className="flex flex-wrap gap-2 pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowResult(false);
                        setPaymentResult(null);
                        setFormError(null);
                      }}
                      className="rounded-lg border border-[#d1d9e0] bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50"
                    >
                      Create another
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('/transactions')}
                      className="rounded-lg px-4 py-2.5 text-sm font-semibold text-white hover:opacity-95"
                      style={{ backgroundColor: BRAND }}
                    >
                      View transactions
                    </button>
                  </div>
                </div>
              )
            )}
          </div>

          {!showResult && (
            <div className="flex shrink-0 justify-end gap-3 border-t border-[#e2e8f0] bg-white px-5 py-4 sm:px-6">
              <button
                type="button"
                onClick={handleClose}
                disabled={submitting}
                className="rounded-lg border border-[#d1d9e0] bg-white px-5 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="create-payment-link-form"
                disabled={submitting || !fiatAmount || !userId}
                className="rounded-lg px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:opacity-95 disabled:opacity-50"
                style={{ backgroundColor: BRAND }}
              >
                {submitting ? (
                  <span className="inline-flex items-center gap-2">
                    <Spinner animation="border" size="sm" className="!w-4 !h-4 border-white" />
                    Creating...
                  </span>
                ) : (
                  'Create Payment Link'
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreatePaymentLinkPage;
